import React, { useMemo, useState } from 'react';
import { SearchIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { mockTickets, Ticket, Priority, Status } from '../data/mockData';
import { TicketDetailModal } from './TicketDetailModal';
const getPriorityColor = (priority: Priority) => {
  switch (priority) {
    case 'Critical':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'High':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'Low':
      return 'bg-green-100 text-green-700 border-green-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};
const getStatusColor = (status: Status) => {
  switch (status) {
    case 'Open':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'In Progress':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'Resolved':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'Overdue':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'Waiting on User':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'Closed':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};
export function TicketTable() {
  const [tickets, setTickets] = useState<Ticket[]>(mockTickets);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Ticket;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    department: ''
  });
  const handleSort = (key: keyof Ticket) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({
      key,
      direction
    });
  };
  const handleUpdateTicket = (updatedTicket: Ticket) => {
    setTickets(tickets.map(t => t.id === updatedTicket.id ? updatedTicket : t));
    setSelectedTicket(updatedTicket);
  };
  const filteredTickets = useMemo(() => {
    let result = [...tickets];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => t.subject.toLowerCase().includes(query) || t.id.toLowerCase().includes(query) || t.submittedBy.toLowerCase().includes(query));
    }
    if (filters.status) result = result.filter(t => t.status === filters.status);
    if (filters.priority) result = result.filter(t => t.priority === filters.priority);
    if (filters.department) result = result.filter(t => t.department === filters.department);
    if (sortConfig) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [tickets, searchQuery, filters, sortConfig]);
  const SortIcon = ({
    columnKey
  }: {
    columnKey: keyof Ticket;
  }) => {
    if (sortConfig?.key !== columnKey) return <ChevronDownIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" data-id="element-175" />;
    return sortConfig.direction === 'asc' ? <ChevronUpIcon className="w-4 h-4 text-blue-600" data-id="element-176" /> : <ChevronDownIcon className="w-4 h-4 text-blue-600" data-id="element-177" />;
  };
  return <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-178">
        <div className="p-5 border-b border-gray-100" data-id="element-179">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-180">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-181">
              Ticket Management Workspace
            </h2>
            <div className="flex items-center space-x-3" data-id="element-182">
              <div className="relative" data-id="element-183">
                <SearchIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" data-id="element-184" />
                <input type="text" placeholder="Search tickets..." className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-185" />
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center space-x-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-186">
                <FilterIcon className="w-4 h-4" data-id="element-187" />
                <span data-id="element-188">Filters</span>
              </button>
            </div>
          </div>

          {showFilters && <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-189">
              <div data-id="element-190">
                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-191">
                  Status
                </label>
                <select className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-192">
                  <option value="" data-id="element-193">All Statuses</option>
                  <option value="Open" data-id="element-194">Open</option>
                  <option value="In Progress" data-id="element-195">In Progress</option>
                  <option value="Waiting on User" data-id="element-196">Waiting on User</option>
                  <option value="Resolved" data-id="element-197">Resolved</option>
                  <option value="Overdue" data-id="element-198">Overdue</option>
                </select>
              </div>
              <div data-id="element-199">
                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-200">
                  Priority
                </label>
                <select className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={filters.priority} onChange={e => setFilters({
              ...filters,
              priority: e.target.value
            })} data-id="element-201">
                  <option value="" data-id="element-202">All Priorities</option>
                  <option value="Critical" data-id="element-203">Critical</option>
                  <option value="High" data-id="element-204">High</option>
                  <option value="Medium" data-id="element-205">Medium</option>
                  <option value="Low" data-id="element-206">Low</option>
                </select>
              </div>
              <div data-id="element-207">
                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-208">
                  Department
                </label>
                <select className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={filters.department} onChange={e => setFilters({
              ...filters,
              department: e.target.value
            })} data-id="element-209">
                  <option value="" data-id="element-210">All Departments</option>
                  <option value="Loan Officers" data-id="element-211">Loan Officers</option>
                  <option value="Processors" data-id="element-212">Processors</option>
                  <option value="Closers" data-id="element-213">Closers</option>
                  <option value="Underwriting" data-id="element-214">Underwriting</option>
                  <option value="IT" data-id="element-215">IT</option>
                </select>
              </div>
              <div className="flex items-end" data-id="element-216">
                <button onClick={() => setFilters({
              status: '',
              priority: '',
              department: ''
            })} className="text-sm text-blue-600 hover:text-blue-800 font-medium px-2 py-2" data-id="element-217">
                  Clear Filters
                </button>
              </div>
            </div>}
        </div>

        <div className="overflow-x-auto" data-id="element-218">
          <table className="w-full text-left border-collapse" data-id="element-219">
            <thead data-id="element-220">
              <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500" data-id="element-221">
                <th className="p-4 font-medium cursor-pointer group" onClick={() => handleSort('id')} data-id="element-222">
                  <div className="flex items-center space-x-1" data-id="element-223">
                    <span data-id="element-224">ID</span>
                    <SortIcon columnKey="id" data-id="element-225" />
                  </div>
                </th>
                <th className="p-4 font-medium cursor-pointer group" onClick={() => handleSort('subject')} data-id="element-226">
                  <div className="flex items-center space-x-1" data-id="element-227">
                    <span data-id="element-228">Subject</span>
                    <SortIcon columnKey="subject" data-id="element-229" />
                  </div>
                </th>
                <th className="p-4 font-medium cursor-pointer group" onClick={() => handleSort('submittedBy')} data-id="element-230">
                  <div className="flex items-center space-x-1" data-id="element-231">
                    <span data-id="element-232">Submitted By</span>
                    <SortIcon columnKey="submittedBy" data-id="element-233" />
                  </div>
                </th>
                <th className="p-4 font-medium cursor-pointer group" onClick={() => handleSort('department')} data-id="element-234">
                  <div className="flex items-center space-x-1" data-id="element-235">
                    <span data-id="element-236">Department</span>
                    <SortIcon columnKey="department" data-id="element-237" />
                  </div>
                </th>
                <th className="p-4 font-medium cursor-pointer group" onClick={() => handleSort('priority')} data-id="element-238">
                  <div className="flex items-center space-x-1" data-id="element-239">
                    <span data-id="element-240">Priority</span>
                    <SortIcon columnKey="priority" data-id="element-241" />
                  </div>
                </th>
                <th className="p-4 font-medium cursor-pointer group" onClick={() => handleSort('status')} data-id="element-242">
                  <div className="flex items-center space-x-1" data-id="element-243">
                    <span data-id="element-244">Status</span>
                    <SortIcon columnKey="status" data-id="element-245" />
                  </div>
                </th>
                <th className="p-4 font-medium cursor-pointer group" onClick={() => handleSort('assignedTo')} data-id="element-246">
                  <div className="flex items-center space-x-1" data-id="element-247">
                    <span data-id="element-248">Assigned To</span>
                    <SortIcon columnKey="assignedTo" data-id="element-249" />
                  </div>
                </th>
                <th className="p-4 font-medium cursor-pointer group" onClick={() => handleSort('createdDate')} data-id="element-250">
                  <div className="flex items-center space-x-1" data-id="element-251">
                    <span data-id="element-252">Created</span>
                    <SortIcon columnKey="createdDate" data-id="element-253" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm" data-id="element-254">
              {filteredTickets.length > 0 ? filteredTickets.map(ticket => <tr key={ticket.id} onClick={() => setSelectedTicket(ticket)} className="hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-255">
                    <td className="p-4 font-medium text-gray-900" data-id="element-256">
                      {ticket.id}
                    </td>
                    <td className="p-4 text-gray-700 max-w-xs truncate" title={ticket.subject} data-id="element-257">
                      {ticket.subject}
                    </td>
                    <td className="p-4 text-gray-600" data-id="element-258">
                      <div data-id="element-259">{ticket.submittedBy}</div>
                      <div className="text-xs text-gray-400" data-id="element-260">{ticket.role}</div>
                    </td>
                    <td className="p-4 text-gray-600" data-id="element-261">{ticket.department}</td>
                    <td className="p-4" data-id="element-262">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`} data-id="element-263">
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-264">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`} data-id="element-265">
                        {ticket.status}
                      </span>
                    </td>
                    <td className="p-4 text-gray-600" data-id="element-266">{ticket.assignedTo}</td>
                    <td className="p-4 text-gray-600" data-id="element-267">
                      {new Date(ticket.createdDate).toLocaleDateString()}
                    </td>
                  </tr>) : <tr data-id="element-268">
                  <td colSpan={8} className="p-8 text-center text-gray-500" data-id="element-269">
                    No tickets found matching your criteria.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-100 bg-gray-50 text-sm text-gray-500 flex justify-between items-center" data-id="element-270">
          <span data-id="element-271">
            Showing {filteredTickets.length} of {tickets.length} tickets
          </span>
        </div>
      </div>

      {selectedTicket && <TicketDetailModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onUpdateTicket={handleUpdateTicket} data-id="element-272" />}
    </>;
}